import React from 'react'
import AppFormFooterBar from '../../../components/AppFormFooterBar'
import {
  AcademicCurriculumSection,
  ScheduleTimingSection,
  OrganizationScopeSection,
  FacultyVenueSection,
} from '../../../components/education-form'
import TimetableTipsCard from './components/TimetableTipsCard'
import useTimetableEntryScreen from './hooks/useTimetableEntryScreen'

export default function TimetableEntryScreen() {
  const { state, actions } = useTimetableEntryScreen()
  const { form, isEditMode, loading, saving, options } = state

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <span className="material-symbols-outlined animate-spin text-3xl text-primary">progress_activity</span>
          <p className="text-xs font-semibold text-on-surface-variant">Loading timetable details...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-20">
      {/* ── Top Header ── */}
      <section className="flex flex-col gap-1">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
          {isEditMode ? 'Edit Time Table Entry' : 'Add Time Table Entry'}
        </h1>
        <p className="text-xs text-on-surface-variant">
          Configure recurring weekly lecture timings, faculty assignments, and classroom bookings.
        </p>
      </section>

      {/* ── Main 2-Column Grid: 4 Form Cards (8 cols) + Sticky TIPS Panel (4 cols) ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-start">
        
        {/* Left Column: Form Fields (8 cols) */}
        <div className="flex flex-col gap-6 lg:col-span-8">
          
          {/* ── Card 1: Academic & Curriculum ── */}
          <AcademicCurriculumSection
            showAcademicYear={false}
            course={form.course}
            onCourseChange={(val) => actions.setField('course', val)}
            courseOptions={options.courses}
            subject={form.subject}
            onSubjectChange={(val) => actions.setField('subject', val)}
            subjectOptions={options.subjects}
          />

          {/* ── Card 2: Schedule & Timing ── */}
          <ScheduleTimingSection
            showDay={true}
            day={form.day}
            onDayChange={(val) => actions.setField('day', val)}
            dayOptions={options.days}
            showIgnoreDuplication={true}
            ignoreDuplication={form.ignoreDuplication}
            onIgnoreDuplicationChange={(val) => actions.setField('ignoreDuplication', val)}
            ignoreDuplicationLabel="Ignore Duplication"
            showTime={true}
            planTime={form.planTime}
            onPlanTimeChange={(val) => actions.setField('planTime', val)}
            tillTime={form.tillTime}
            onTillTimeChange={(val) => actions.setField('tillTime', val)}
          />

          {/* ── Card 3: Organization & Target Scope ── */}
          <OrganizationScopeSection
            company={form.company}
            onCompanyChange={(val) => actions.setField('company', val)}
            companyOptions={options.companies}
            branch={form.branch}
            onBranchChange={(val) => actions.setField('branch', val)}
            branchOptions={options.branches}
            showBranchScope={true}
            branchScope={form.branchType}
            onBranchScopeChange={(val) => actions.setField('branchType', val)}
            batch={form.batch}
            onBatchChange={(val) => actions.setField('batch', val)}
            batchOptions={options.batches}
            showBatchScope={true}
            batchScope={form.batchType}
            onBatchScopeChange={(val) => actions.setField('batchType', val)}
            school={form.school}
            onSchoolChange={(val) => actions.setField('school', val)}
            schoolOptions={options.schools}
            showSchoolScope={true}
            schoolScope={form.schoolType}
            onSchoolScopeChange={(val) => actions.setField('schoolType', val)}
          />

          {/* ── Card 4: Faculty, Venue & Delivery ── */}
          <FacultyVenueSection
            faculty={form.faculty}
            onFacultyChange={(val) => actions.setField('faculty', val)}
            facultyOptions={options.faculties}
            classroom={form.classroom}
            onClassroomChange={(val) => actions.setField('classroom', val)}
            classroomOptions={options.classrooms}
            classroomLabel="Classroom"
            classroomPlaceholder="Select Classroom / Lecture Hall..."
          />

        </div>

        {/* Right Column: Tips Card (4 cols) */}
        <div className="lg:col-span-4 sticky top-6">
          <TimetableTipsCard />
        </div>

      </div>

      {/* ── Global Sticky Form Footer Bar ── */}
      <AppFormFooterBar
        cancelPath="/lectures/timetable"
        isSaving={saving}
        showSaveAndContinue={!isEditMode}
        onSaveContinue={() => actions.handleSave(true)}
        onSaveExit={() => actions.handleSave(false)}
        saveLabel={isEditMode ? 'Update Entry' : 'Save & Exit'}
      />
    </div>
  )
}
